import { useState, useEffect, useCallback, useRef } from "react";

/**
 * A hook that provides multi-select functionality for lists of items
 * @param {Array} items - Array of items to be selected
 * @param {Array} interactiveSelectors - Array of selectors that should not trigger selection/deselection when clicked
 * @param {string} group - This is the name of the selectable group i.e worldbuilding, characters, etc. It should be the same as the value of the data-selectable attribute on each of the items.
 * @returns {Object} - Selection state and handler functions
 */
export const useMultiSelect = (items, interactiveSelectors = [], group = null) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [lastSelectedItemId, setLastSelectedItemId] = useState(null);
  const itemsRef = useRef(items);
  const selectedItemsRef = useRef(selectedItems);
  const lastSelectedItemIdRef = useRef(lastSelectedItemId);
  const interactiveSelectorsRef = useRef(interactiveSelectors);
  const groupRef = useRef(group);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    selectedItemsRef.current = selectedItems;
  }, [selectedItems]);

  useEffect(() => {
    lastSelectedItemIdRef.current = lastSelectedItemId;
  }, [lastSelectedItemId]);

  useEffect(() => {
    interactiveSelectorsRef.current = interactiveSelectors;
  }, [interactiveSelectors]);

  useEffect(() => {
    groupRef.current = group;
  }, [group]);

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
    setLastSelectedItemId(null);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        clearSelection();
      }
    },
    [clearSelection],
  );

  const handleContainerClick = useCallback(
    (e) => {
      const isInteractiveElement = interactiveSelectorsRef.current.some((selector) => e.target.closest(selector));
      const isSelectableItem = e.target.closest(`[data-selectable="${groupRef.current}"]`);

      if (!isInteractiveElement && !isSelectableItem) {
        clearSelection();
      }
    },
    [clearSelection],
  );

  useEffect(() => {
    if (selectedItems.length > 0) {
      window.addEventListener("keydown", handleKeyDown);
      document.addEventListener("click", handleContainerClick);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleContainerClick);
    };
  }, [selectedItems, handleKeyDown, handleContainerClick]);

  const clearWindowSelections = () => {
    if (typeof window !== "undefined" && typeof window.getSelection === "function") {
      const selection = window.getSelection();
      if (selection) {
        // different browsers have different methods for clearing the selection
        if (typeof selection.empty === "function") {
          selection.empty();
        } else if (typeof selection.removeAllRanges === "function") {
          selection.removeAllRanges();
        }
      }
    }

    // Remove focus from any focused elements during multi-select
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur(); // not sure about this
    }
  };

  const handleSelect = useCallback((item, isShiftKey = false, isCtrlOrCmdKey = false, e) => {
    const isInteractiveElement = interactiveSelectorsRef.current.some((selector) => e.target.closest(selector));

    if (isInteractiveElement) {
      return;
    }

    // Clear any unwanted text selection that might have occurred
    if (isShiftKey || isCtrlOrCmdKey) {
      clearWindowSelections();
    }

    const currentItems = itemsRef.current;
    const currentSelection = selectedItemsRef.current;
    const currentLastSelectedItemId = lastSelectedItemIdRef.current;
    const isItemAlreadySelected = currentSelection.some((selected) => selected.id === item.id);

    if (isItemAlreadySelected) {
      const newSelection = currentSelection.filter((selected) => selected.id !== item.id);
      setSelectedItems(newSelection);

      if (currentLastSelectedItemId === item.id && newSelection.length > 0) {
        setLastSelectedItemId(newSelection[newSelection.length - 1].id);
      } else if (newSelection.length === 0) {
        setLastSelectedItemId(null);
      }
      return;
    }

    if (isShiftKey && currentSelection.length > 0 && currentLastSelectedItemId) {
      const lastSelectedIndex = currentItems.findIndex((i) => i.id === currentLastSelectedItemId);
      const currentIndex = currentItems.findIndex((itemToFind) => itemToFind.id === item.id);

      if (lastSelectedIndex !== -1 && currentIndex !== -1) {
        const startIdx = Math.min(lastSelectedIndex, currentIndex);
        const endIdx = Math.max(lastSelectedIndex, currentIndex);
        const itemsToSelect = currentItems.slice(startIdx, endIdx + 1);
        const newSelection = [...currentSelection];

        itemsToSelect.forEach((itemToAdd) => {
          if (!newSelection.some((selected) => selected.id === itemToAdd.id)) {
            newSelection.push(itemToAdd);
          }
        });

        setSelectedItems(newSelection);
      }
      return;
    }

    if (isCtrlOrCmdKey) {
      setSelectedItems([...currentSelection, item]);
      setLastSelectedItemId(item.id);
      return;
    }

    setSelectedItems([item]);
    setLastSelectedItemId(item.id);
  }, []);

  const handleDeselect = useCallback((item) => {
    const currentSelection = selectedItemsRef.current;
    const currentLastSelectedItemId = lastSelectedItemIdRef.current;
    const newSelection = currentSelection.filter((selected) => selected.id !== item.id);
    setSelectedItems(newSelection);

    if (currentLastSelectedItemId === item.id && newSelection.length > 0) {
      setLastSelectedItemId(newSelection[newSelection.length - 1].id);
    } else if (newSelection.length === 0) {
      setLastSelectedItemId(null);
    }
  }, []);

  const isSelected = useCallback(
    (itemId) => {
      return selectedItems.some((item) => item.id === itemId);
    },
    [selectedItems],
  );

  return {
    selectedItems,
    handleSelect,
    handleDeselect,
    handleContainerClick,
    isSelected,
    clearSelection,
  };
};
